<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';
import {
  input,
  qualificationAnswerTypeOptions,
  qualificationOperationOptions,
} from '../utils/minhaLaisOptions';
import { qualificationQuestionNeedsOptions } from '../utils/minhaLaisPresenters';

defineProps({
  qualificationQuestionLimits: { type: Object, required: true },
  isLoadingQualificationQuestions: { type: Boolean, default: false },
  canUpdateSettings: { type: Boolean, default: false },
  savingQualificationQuestionId: { type: [Number, String], default: null },
  deletingQualificationQuestionId: { type: [Number, String], default: null },
});

const emit = defineEmits(['refresh', 'create', 'save', 'remove']);

const qualificationQuestions = defineModel('qualificationQuestions', {
  type: Array,
  required: true,
});

const newQualificationQuestion = defineModel('newQualificationQuestion', {
  type: Object,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Perguntas essenciais">
    <div
      class="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="flex flex-wrap gap-2">
        <span
          class="inline-flex items-center rounded-full bg-[#f8f4fc] px-3 py-1 text-[12px] font-semibold text-lais-primary"
        >
          {{ qualificationQuestionLimits.active_essential }}/{{
            qualificationQuestionLimits.essential
          }}
          essenciais
        </span>
        <span
          class="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-[12px] font-semibold text-amber-700"
        >
          {{ qualificationQuestionLimits.active_qualifying }}/{{
            qualificationQuestionLimits.qualifying
          }}
          qualificatórias
        </span>
      </div>
      <button
        type="button"
        class="inline-flex h-9 items-center justify-center gap-2 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isLoadingQualificationQuestions"
        @click="emit('refresh')"
      >
        <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
        Atualizar
      </button>
    </div>

    <div
      v-if="isLoadingQualificationQuestions"
      class="flex items-center gap-2 rounded-lais border border-[#e5e7eb] bg-[#faf8fd] p-4 text-[13px] text-[#6b7280]"
    >
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      Carregando perguntas...
    </div>

    <template v-else>
      <div
        v-if="!canUpdateSettings"
        class="mb-4 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-3 text-[13px] text-lais-text-body"
      >
        Apenas administradores podem alterar perguntas.
      </div>

      <div
        v-if="canUpdateSettings"
        class="mb-5 grid gap-4 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-4"
      >
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <LaisField label="Pergunta">
            <input
              v-model="newQualificationQuestion.label"
              type="text"
              :class="input"
              placeholder="Ex.: Qual garantia você pretende usar?"
            />
          </LaisField>
          <LaisField label="Chave">
            <input
              v-model="newQualificationQuestion.key"
              type="text"
              :class="input"
              placeholder="Ex.: tipo_garantia"
            />
          </LaisField>
          <LaisField label="Escopo">
            <LaisSelect
              v-model="newQualificationQuestion.operation_scope"
              :options="qualificationOperationOptions"
            />
          </LaisField>
          <LaisField label="Tipo de resposta">
            <LaisSelect
              v-model="newQualificationQuestion.answer_type"
              :options="qualificationAnswerTypeOptions"
            />
          </LaisField>
        </div>
        <LaisField label="Orientação para IA">
          <textarea
            v-model="newQualificationQuestion.prompt_hint"
            rows="2"
            :class="input"
          />
        </LaisField>
        <LaisField
          v-if="qualificationQuestionNeedsOptions(newQualificationQuestion)"
          label="Opções"
        >
          <textarea
            v-model="newQualificationQuestion.options_text"
            rows="3"
            :class="input"
            placeholder="Fiador
Seguro fiança
Caução"
          />
        </LaisField>
        <div class="flex flex-wrap items-center gap-4">
          <label class="inline-flex items-center gap-2 cursor-pointer">
            <input
              v-model="newQualificationQuestion.active"
              type="checkbox"
              class="size-4 accent-lais-primary"
            />
            <span class="text-[13px] font-semibold text-lais-text">Ativa</span>
          </label>
          <label class="inline-flex items-center gap-2 cursor-pointer">
            <input
              v-model="newQualificationQuestion.essential"
              type="checkbox"
              class="size-4 accent-lais-primary"
            />
            <span class="text-[13px] font-semibold text-lais-text">
              Essencial
            </span>
          </label>
          <label class="inline-flex items-center gap-2 cursor-pointer">
            <input
              v-model="newQualificationQuestion.qualifying"
              type="checkbox"
              class="size-4 accent-lais-primary"
            />
            <span class="text-[13px] font-semibold text-lais-text">
              Qualificatória
            </span>
          </label>
          <button
            type="button"
            class="ml-auto inline-flex h-9 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#3d2966] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="savingQualificationQuestionId === 'new'"
            @click="emit('create')"
          >
            <span class="i-lucide-plus size-4" aria-hidden="true" />
            Adicionar
          </button>
        </div>
      </div>

      <div v-if="qualificationQuestions.length" class="grid gap-3">
        <div
          v-for="question in qualificationQuestions"
          :key="question.id"
          class="grid gap-4 rounded-lais-sm border border-[#eee5d6] p-4"
        >
          <div
            class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span class="truncate text-[14px] font-semibold text-lais-text">
                  {{ question.label }}
                </span>
                <span
                  v-if="question.qualifying"
                  class="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
                >
                  Qualificatória
                </span>
                <span
                  v-if="!question.active"
                  class="rounded-full bg-[#f5f5f5] px-2 py-0.5 text-[11px] font-semibold text-lais-text-body"
                >
                  Inativa
                </span>
              </div>
              <p class="mt-1 font-mono text-[12px] text-[#6b7280]">
                {{ question.key }}
              </p>
            </div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="
                  !canUpdateSettings ||
                  savingQualificationQuestionId === question.id
                "
                @click="emit('save', question)"
              >
                <span class="i-lucide-save size-3.5" aria-hidden="true" />
                Salvar
              </button>
              <button
                v-if="canUpdateSettings"
                type="button"
                class="inline-flex h-8 items-center justify-center rounded-lais-sm border border-[#ffd6d6] px-2 text-[#b42318] transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="deletingQualificationQuestionId === question.id"
                aria-label="Remover pergunta"
                @click="emit('remove', question)"
              >
                <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <LaisField label="Pergunta">
              <input
                v-model="question.label"
                type="text"
                :class="input"
                :disabled="!canUpdateSettings"
              />
            </LaisField>
            <LaisField label="Chave">
              <input
                v-model="question.key"
                type="text"
                :class="input"
                :disabled="!canUpdateSettings"
              />
            </LaisField>
            <LaisField label="Escopo">
              <LaisSelect
                v-model="question.operation_scope"
                :options="qualificationOperationOptions"
                :disabled="!canUpdateSettings"
              />
            </LaisField>
            <LaisField label="Tipo de resposta">
              <LaisSelect
                v-model="question.answer_type"
                :options="qualificationAnswerTypeOptions"
                :disabled="!canUpdateSettings"
              />
            </LaisField>
          </div>
          <LaisField label="Orientação para IA">
            <textarea
              v-model="question.prompt_hint"
              rows="2"
              :class="input"
              :disabled="!canUpdateSettings"
            />
          </LaisField>
          <LaisField
            v-if="qualificationQuestionNeedsOptions(question)"
            label="Opções"
          >
            <textarea
              v-model="question.options_text"
              rows="3"
              :class="input"
              :disabled="!canUpdateSettings"
            />
          </LaisField>
          <div class="flex flex-wrap items-center gap-4">
            <label class="inline-flex items-center gap-2 cursor-pointer">
              <input
                v-model="question.active"
                type="checkbox"
                class="size-4 accent-lais-primary"
                :disabled="!canUpdateSettings"
              />
              <span class="text-[13px] font-semibold text-lais-text">
                Ativa
              </span>
            </label>
            <label class="inline-flex items-center gap-2 cursor-pointer">
              <input
                v-model="question.essential"
                type="checkbox"
                class="size-4 accent-lais-primary"
                :disabled="!canUpdateSettings"
              />
              <span class="text-[13px] font-semibold text-lais-text">
                Essencial
              </span>
            </label>
            <label class="inline-flex items-center gap-2 cursor-pointer">
              <input
                v-model="question.qualifying"
                type="checkbox"
                class="size-4 accent-lais-primary"
                :disabled="!canUpdateSettings"
              />
              <span class="text-[13px] font-semibold text-lais-text">
                Qualificatória
              </span>
            </label>
            <LaisField label="Ordem" class="w-28">
              <input
                v-model.number="question.position"
                type="number"
                min="1"
                :class="input"
                :disabled="!canUpdateSettings"
              />
            </LaisField>
          </div>
        </div>
      </div>

      <div
        v-else
        class="rounded-lais-sm border border-dashed border-[#d7c8ef] bg-[#faf8fd] p-5 text-center text-[13px] text-[#6b7280]"
      >
        Nenhuma pergunta configurada.
      </div>
    </template>
  </LaisCard>
</template>
